import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import useSupabaseClient from '../hooks/useSupabaseClient';
import DraggableDialogPaper from './DraggableDialogPaper';
import Autocomplete from '@mui/material/Autocomplete';

const parseRanges = (value) => {
  const str = String(value || '').trim();
  if (!str) return [];
  return str
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((part) => {
      const [from, to] = part.split('-').map((v) => (v ?? '').trim());
      return { from: from || '', to: to || '' };
    })
    .filter((r) => r.from || r.to);
};

const serializeRanges = (ranges) => {
  return (ranges || [])
    .map((r) => {
      const a = String(r?.from ?? '').trim();
      const b = String(r?.to ?? '').trim();
      if (!a && !b) return '';
      if (a && b) return `${a}-${b}`;
      return a || b;
    })
    .filter(Boolean)
    .join(',');
};

const RangeListEditor = ({ label, value, onChange, helperText }) => {
  const [rows, setRows] = useState(() => parseRanges(value));
  const listRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    setRows(parseRanges(value));
  }, [value]);

  const updateAt = (idx, patch) => {
    setRows((prev) => {
      const next = (prev || []).map((r, i) => (i === idx ? { ...r, ...patch } : r));
      onChange(serializeRanges(next));
      return next;
    });
  };

  const removeAt = (idx) => {
    setRows((prev) => {
      const next = (prev || []).filter((_r, i) => i !== idx);
      onChange(serializeRanges(next));
      return next;
    });
  };

  const addRange = () => {
    setRows((prev) => {
      const next = [...(prev || []), { from: '', to: '' }];
      return next;
    });
    setTimeout(() => {
      if (endRef.current && typeof endRef.current.scrollIntoView === 'function') {
        endRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
      } else if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    }, 0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addRange}>
          Agregar
        </Button>
      </Box>
      {helperText ? (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      ) : null}

      <Box
        ref={listRef}
        sx={{
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: 220,
          overflowY: 'auto',
          pr: 1,
        }}
      >
        {rows.length ? (
          rows.map((r, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Desde"
                size="small"
                value={r.from}
                onChange={(e) => updateAt(idx, { from: e.target.value })}
                fullWidth
              />
              <TextField
                label="Hasta"
                size="small"
                value={r.to}
                onChange={(e) => updateAt(idx, { to: e.target.value })}
                fullWidth
              />
              <IconButton onClick={() => removeAt(idx)} size="small" color="error">
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin rangos configurados
          </Typography>
        )}
        <Box ref={endRef} />
      </Box>

      <TextField
        label="Valor en texto"
        size="small"
        value={String(value || '')}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        sx={{ mt: 1 }}
      />
    </Box>
  );
};

const InvoiceLocalServiceConfigModal = ({ open, onClose, user, erpListsStore }) => {
  const { supabase } = useSupabaseClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [configRow, setConfigRow] = useState(null);

  const [erpData, setErpData] = useState(erpListsStore ? erpListsStore.getState() : { locations: [], costCenters: [], departments: [] });
  useEffect(() => {
    if (erpListsStore) {
      const unsub = erpListsStore.subscribe(setErpData);
      return unsub;
    }
  }, [erpListsStore]);

  useEffect(() => {
    const load = async () => {
      if (!open) return;
      if (!supabase) return;
      if (!user?.id) return;

      setIsLoading(true);
      setError('');
      try {
        const { data, error: dbError } = await supabase
          .from('invoice_config')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (dbError) {
          setConfigRow(null);
          throw dbError;
        }

        setConfigRow(data);
      } catch (e) {
        console.error('Error loading invoice_config:', e);
        setError('No se pudo cargar la configuración. Verifica que exista un registro para este usuario.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [open, supabase, user?.id]);

  const updateField = (field, value) => {
    setConfigRow((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!supabase) return;
    if (!user?.id) return;
    if (!configRow) return;

    setIsSaving(true);
    setError('');
    try {
      const payload = { ...configRow };
      delete payload.id;
      delete payload.user_id;
      delete payload.created_at;
      delete payload.updated_at;

      const { error: dbError } = await supabase
        .from('invoice_config')
        .update(payload)
        .eq('user_id', user.id);

      if (dbError) throw dbError;
      onClose();
    } catch (e) {
      console.error('Error saving invoice_config:', e);
      setError('No se pudo guardar la configuración.');
    } finally {
      setIsSaving(false);
    }
  };

  const disabled = isLoading || isSaving;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperComponent={DraggableDialogPaper}>
      <DialogTitle className="draggable-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'move' }}>
        Configuración de servicio local
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflowX: 'hidden' }}>
        {error ? (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        ) : null}

        {!configRow ? (
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Cargando...' : 'Sin configuración para este usuario.'}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Configuración de Firebird
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Servidor (host)"
                    value={configRow.firebird_host ?? ''}
                    onChange={(e) => updateField('firebird_host', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Puerto"
                    value={configRow.firebird_port ?? ''}
                    onChange={(e) => updateField('firebird_port', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Ruta de la base de datos"
                    value={configRow.firebird_database ?? ''}
                    onChange={(e) => updateField('firebird_database', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Usuario"
                    value={configRow.firebird_user ?? ''}
                    onChange={(e) => updateField('firebird_user', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Contraseña"
                    type="password"
                    value={configRow.firebird_password ?? ''}
                    onChange={(e) => updateField('firebird_password', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Configuración del servicio
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nombre del servicio"
                    value={configRow.service_name ?? ''}
                    onChange={(e) => updateField('service_name', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nivel de logs"
                    value={configRow.log_level ?? ''}
                    onChange={(e) => updateField('log_level', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Puerto del API"
                    value={configRow.api_port ?? ''}
                    onChange={(e) => updateField('api_port', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">:</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.use_invoice_number_for_invc)}
                        onChange={(e) => updateField('use_invoice_number_for_invc', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Usar número de factura del proveedor"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.use_header_description_for_detail)}
                        onChange={(e) => updateField('use_header_description_for_detail', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Reemplazar la descripción del detalle por la del encabezado"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Proyecto / Actividad por defecto
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Proyecto por defecto"
                    value={configRow.default_project_code ?? ''}
                    onChange={(e) => updateField('default_project_code', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Máximo 10 caracteres"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Actividad por defecto"
                    value={configRow.default_activity_code ?? ''}
                    onChange={(e) => updateField('default_activity_code', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Máximo 3 caracteres"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Tipo de documento
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Tipo de documento (Facturas)"
                    value={configRow.document_type ?? ''}
                    onChange={(e) => updateField('document_type', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Máximo 3 caracteres"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Tipo de documento (Entrada a almacén)"
                    value={configRow.ea_document_type ?? ''}
                    onChange={(e) => updateField('ea_document_type', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Máximo 3 caracteres"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Tipo de documento (Orden de compra)"
                    value={configRow.oc_document_type ?? ''}
                    onChange={(e) => updateField('oc_document_type', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Máximo 3 caracteres"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Tipo de documento (Cuenta de cobro)"
                    value={configRow.cc_document_type ?? ''}
                    onChange={(e) => updateField('cc_document_type', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Máximo 3 caracteres"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Sincronización de inventario
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                  <Autocomplete
                    size="small"
                    options={erpData.locations || []}
                    getOptionLabel={(option) => typeof option === 'string' ? option : `${option.codigo} - ${option.descripcion}`}
                    isOptionEqualToValue={(option, value) => option.codigo === value || option.codigo === value?.codigo}
                    value={(erpData.locations || []).find(l => l.codigo === configRow.default_warehouse_code) || configRow.default_warehouse_code || null}
                    onChange={(e, newValue) => {
                      const newCode = newValue ? (typeof newValue === 'string' ? newValue : newValue.codigo) : '';
                      updateField('default_warehouse_code', newCode);
                    }}
                    freeSolo
                    disabled={disabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Bodega por defecto"
                        helperText="Bodega de destino para las líneas (Máx 3 caracteres). Ej: 01"
                        sx={{ backgroundColor: 'white' }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.sync_ea)}
                        onChange={(e) => updateField('sync_ea', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Sincronizar inventario a Entradas de Almacén (EA)"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.sync_oc)}
                        onChange={(e) => updateField('sync_oc', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Sincronizar inventario a Órdenes de Compra (OC)"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.contabilizar_ea)}
                        onChange={(e) => updateField('contabilizar_ea', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Contabilizar Entradas de Almacén automáticamente"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Modelos de Inteligencia Artificial (ML)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Días de retención para entrenamiento ML"
                    value={configRow.ml_training_retention_days ?? 365}
                    onChange={(e) => updateField('ml_training_retention_days', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Tiempo que se guardará la historia (ej: 365 días)"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Sincronización en segundo plano
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Intervalo de sincronización de terceros"
                    value={configRow.third_parties_sync_interval ?? ''}
                    onChange={(e) => updateField('third_parties_sync_interval', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Minutos"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Retraso inicial al arrancar"
                    value={configRow.initial_sync_delay ?? ''}
                    onChange={(e) => updateField('initial_sync_delay', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Minutos"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.enable_auto_third_party_creation)}
                        onChange={(e) => updateField('enable_auto_third_party_creation', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Crear terceros automáticamente si no existen"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.enable_invoice_polling ?? true)}
                        onChange={(e) => updateField('enable_invoice_polling', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Habilitar sincronización de respaldo (Polling)"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Intervalo de sincronización de respaldo"
                    value={configRow.invoice_polling_interval ?? 5}
                    onChange={(e) => updateField('invoice_polling_interval', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Minutos"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Intervalo de sincronización de Órdenes de Compra pendientes"
                    value={configRow.pending_orders_sync_interval ?? 0}
                    onChange={(e) => updateField('pending_orders_sync_interval', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Minutos"
                  />
                </Grid>
              </Grid>
            </Box>
            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Sincronización de anticipos de proveedores
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.enable_advances_sync)}
                        onChange={(e) => updateField('enable_advances_sync', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Sincronizar saldos de anticipos de proveedores"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Intervalo de sincronización (minutos)"
                    value={configRow.advances_sync_interval ?? 60}
                    onChange={(e) => updateField('advances_sync_interval', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Cruce de Entradas de Almacén (EA)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.enable_ea_sync)}
                        onChange={(e) => updateField('enable_ea_sync', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Habilitar sincronización de EAs pendientes"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Cuenta puente EA"
                    value={configRow.ea_bridge_account ?? ''}
                    onChange={(e) => updateField('ea_bridge_account', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Ej: 23300501"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Intervalo de sincronización (minutos)"
                    value={configRow.ea_entries_sync_interval ?? 0}
                    onChange={(e) => updateField('ea_entries_sync_interval', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Sincronización de cuentas contables
              </Typography>
              <Grid container spacing={2}>
                {/* Rangos ocultos: ahora se sincroniza la totalidad del PUC */}
                {/* 
                <Grid item xs={12} md={6}>
                  <RangeListEditor
                    label="Rangos de cuentas a sincronizar"
                    value={configRow.account_sync_ranges ?? ''}
                    onChange={(v) => updateField('account_sync_ranges', v)}
                    helperText="Ej: 22000000-23999999"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RangeListEditor
                    label="Rangos de cuentas a excluir"
                    value={configRow.account_exclude_ranges ?? ''}
                    onChange={(v) => updateField('account_exclude_ranges', v)}
                    helperText="Ej: 23670100-23670100"
                  />
                </Grid> 
                */}
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.sync_only_active_accounts)}
                        onChange={(e) => updateField('sync_only_active_accounts', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Sincronizar solo cuentas activas"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.exclude_zero_level_accounts)}
                        onChange={(e) => updateField('exclude_zero_level_accounts', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Excluir cuentas de nivel 0"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Intervalo de sincronización del plan de cuentas"
                    value={configRow.chart_of_accounts_sync_interval ?? ''}
                    onChange={(e) => updateField('chart_of_accounts_sync_interval', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Minutos"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Sincronización de productos
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Intervalo de sincronización de productos"
                    value={configRow.products_sync_interval ?? ''}
                    onChange={(e) => updateField('products_sync_interval', Number(e.target.value) || 0)}
                    fullWidth
                    size="small"
                    disabled={disabled}
                    helperText="Minutos"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.sync_only_active_products)}
                        onChange={(e) => updateField('sync_only_active_products', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Sincronizar solo productos activos"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'none' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(configRow.sync_only_inventory_products)}
                        onChange={(e) => updateField('sync_only_inventory_products', e.target.checked)}
                        disabled={disabled}
                      />
                    }
                    label="Sincronizar solo productos de inventario"
                    sx={{ alignItems: 'flex-start', '& .MuiFormControlLabel-label': { whiteSpace: 'normal' } }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cerrar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!configRow || disabled}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceLocalServiceConfigModal;
